import { lazy } from "react";
// Note: make sure path is in lower case

export const ROUTES = {
  LOGIN: "/",
  DASHBOARD: "/dashboard",
  PENDING_USERS: "/users",
  /**
   * Sub-page of the Users tab. Reached via the "Activate pending account" CTA
   * on `/users`; intentionally not in the bottom-tab bar to avoid confusing
   * the day-to-day technician with a flow that's only used occasionally.
   */
  ACTIVATE_PENDING: "/users/activate",
  /**
   * Sub-page of the Users tab. Opened by tapping a registered-user card on
   * `/users` or a recent-setup row on the dashboard. The full `IClinicUser`
   * is passed via `location.state.user` so the page renders synchronously
   * without a fetch — refreshing here bounces back to `/users` (no single-user
   * GET endpoint is available yet).
   *
   * Sits alongside `/users/activate`: React Router v6 prefers literal
   * segments, so `/users/activate` wins over `/users/:userId` for that path.
   */
  USER_DETAIL: "/users/:userId",
  PROFILE: "/profile",
  SETUP_STEP_1: "/setup/:patientId/1",
  SETUP_STEP_2: "/setup/:patientId/2",
  SETUP_STEP_3: "/setup/:patientId/3",
  SETUP_STEP_4: "/setup/:patientId/4",
  SETUP_COMPLETE: "/setup/:patientId/complete",
} as const;

// Step mapping (4 steps total):
// /1 → Step1Account   (create account)
// /2 → Step3Device    (OCR + portal)
// /3 → Step4Verify (hidden when FIELD_SETUP_VERIFY_STEP_ENABLED is false in fieldSetupFlags.ts)
// /4 → Step5Handover  (patient handover)

export const routes = {
  login: {
    index: true,
    element: lazy(() => import("../../pages/Login")),
  },
  dashboard: {
    path: ROUTES.DASHBOARD,
    element: lazy(() => import("../../pages/Dashboard")),
  },
  pendingUsers: {
    path: ROUTES.PENDING_USERS,
    element: lazy(() => import("../../pages/PendingUsers")),
  },
  activatePending: {
    path: ROUTES.ACTIVATE_PENDING,
    element: lazy(() => import("../../pages/PendingActivation")),
  },
  userDetail: {
    path: ROUTES.USER_DETAIL,
    element: lazy(() => import("../../pages/UserDetail")),
  },
  profile: {
    path: ROUTES.PROFILE,
    element: lazy(() => import("../../pages/Profile")),
  },
  setupStep1: {
    path: ROUTES.SETUP_STEP_1,
    element: lazy(() => import("../../pages/Setup/Step1Account")),
  },
  setupStep2: {
    path: ROUTES.SETUP_STEP_2,
    element: lazy(() => import("../../pages/Setup/Step3Device")),
  },
  setupStep3: {
    path: ROUTES.SETUP_STEP_3,
    element: lazy(() => import("../../pages/Setup/Step4Verify")),
  },
  setupStep4: {
    path: ROUTES.SETUP_STEP_4,
    element: lazy(() => import("../../pages/Setup/Step5Handover")),
  },
  setupComplete: {
    path: ROUTES.SETUP_COMPLETE,
    element: lazy(() => import("../../pages/Complete")),
  },
  catchAll: {
    path: "*",
    element: lazy(() => import("./CatchAllRedirect")),
  },
} as const;
